import { Component, OnDestroy, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { OtpService } from '../../../../core/services/otp.service';
import { RegistrationService } from '../../../../core/services/registration.service';
import { ResetPasswordPayload } from '../../../../core/models/registration.models';
import { PASSWORD_HINT, PASSWORD_PATTERN } from '../../../../core/utils/registration.utils';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { getPhoneValidationError, resolvePhoneCredentials } from '../../../../shared/utils/phone.utils';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnDestroy {
  @ViewChild('phoneInput') phoneInput?: PhoneInputComponent;

  readonly passwordHint = PASSWORD_HINT;

  currentStep = 0;
  loading = false;
  hidePassword = true;
  hideConfirmPassword = true;
  errorMessage = '';
  successMessage = '';

  resetPayload: ResetPasswordPayload | null = null;
  prefixCountry = '+237';
  otpPhoneDisplay = '';
  otpResendSeconds = 0;
  private otpTimerSub?: Subscription;

  readonly form = this.fb.group({
    phone: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
    confirmPassword: ['', [Validators.required]],
    otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly otpService: OtpService,
    private readonly registrationService: RegistrationService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {
    this.form.controls.confirmPassword.addValidators(
      this.passwordMatchValidator.bind(this)
    );
  }

  ngOnDestroy(): void {
    this.otpTimerSub?.unsubscribe();
  }

  previousStep(): void {
    this.errorMessage = '';
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = '';

    if (this.currentStep === 0) {
      await this.sendOtp();
      return;
    }

    await this.completeReset();
  }

  resendOtp(): void {
    if (this.otpResendSeconds > 0 || !this.resetPayload) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.otpService.sendOtp(this.resetPayload.username, this.prefixCountry).subscribe({
      next: (result) => {
        this.loading = false;
        if (result.status === 'SUCCESS') {
          this.startOtpTimer();
          return;
        }
        this.errorMessage = this.getOtpSendErrorMessage(result.status);
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Impossible de renvoyer le code OTP.';
      },
    });
  }

  getPhoneError(): string | null {
    const control = this.form.controls.phone;
    if (!control.touched || control.valid) {
      return null;
    }

    if (control.hasError('required')) {
      return 'Le téléphone est requis';
    }

    return getPhoneValidationError(control.errors?.['invalidPhone']);
  }

  private async sendOtp(): Promise<void> {
    const fields = ['phone', 'password', 'confirmPassword'] as const;
    let valid = true;
    for (const field of fields) {
      const control = this.form.get(field);
      control?.markAsTouched();
      if (control?.invalid) {
        valid = false;
      }
    }
    if (!valid) {
      return;
    }

    const { phone, password } = this.form.getRawValue();
    const { username, dialCode } = resolvePhoneCredentials(
      phone!,
      this.phoneInput?.getInstance()
    );
    this.prefixCountry = dialCode;
    this.otpPhoneDisplay = phone!;
    this.resetPayload = { username, password: password! };

    this.loading = true;

    try {
      const result = await firstValueFrom(
        this.otpService.sendOtp(username, this.prefixCountry)
      );

      if (result.status !== 'SUCCESS') {
        this.errorMessage = this.getOtpSendErrorMessage(result.status);
        return;
      }

      this.currentStep = 1;
      this.startOtpTimer();
    } catch {
      this.errorMessage = 'Impossible d’envoyer le code OTP. Réessayez plus tard.';
    } finally {
      this.loading = false;
    }
  }

  private async completeReset(): Promise<void> {
    const otpControl = this.form.controls.otp;
    otpControl.markAsTouched();
    if (otpControl.invalid || !this.resetPayload) {
      return;
    }

    this.loading = true;

    try {
      const verifyResult = await firstValueFrom(
        this.otpService.verifyOtp(this.resetPayload.username, otpControl.value!)
      );

      if (verifyResult.status !== 'OTP_OK') {
        this.errorMessage =
          verifyResult.status === 'OTP_ESPIRED'
            ? 'Le code OTP a expiré. Demandez un nouveau code.'
            : verifyResult.message ?? 'Code OTP invalide.';
        return;
      }

      const resetResult = await firstValueFrom(
        this.registrationService.resetPassword(this.resetPayload)
      );

      if (resetResult.status === 'USERNAME_NOT_EXIST') {
        this.errorMessage = 'Aucun compte associé à ce numéro de téléphone.';
        return;
      }

      if (resetResult.status !== 'SUCCESS') {
        this.errorMessage =
          resetResult.message ?? 'La réinitialisation du mot de passe a échoué.';
        return;
      }

      const loginResult = await this.authService.login(
        this.resetPayload.username,
        this.resetPayload.password,
        false,
        this.prefixCountry
      );

      if (loginResult.status === 'SUCCESS') {
        this.router.navigate(['/profil']);
        return;
      }

      this.successMessage = 'Mot de passe mis à jour. Connectez-vous avec vos nouveaux identifiants.';
      this.router.navigate(['/se-connecter']);
    } catch {
      this.errorMessage = 'Une erreur est survenue lors de la réinitialisation.';
    } finally {
      this.loading = false;
    }
  }

  private startOtpTimer(): void {
    this.otpTimerSub?.unsubscribe();
    this.otpResendSeconds = 60;

    const intervalId = window.setInterval(() => {
      this.otpResendSeconds--;
      if (this.otpResendSeconds <= 0) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    this.otpTimerSub = new Subscription(() => window.clearInterval(intervalId));
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = this.form?.controls.password.value;
    if (!control.value || control.value === password) {
      return null;
    }
    return { passwordMismatch: true };
  }

  private getOtpSendErrorMessage(status: string): string {
    switch (status) {
      case 'OFF_SERVICE':
      case 'OTP_NOT_SAVE':
        return 'Service SMS indisponible pour l’instant. Réessayez plus tard.';
      default:
        return 'Impossible d’envoyer le code OTP.';
    }
  }
}
